/**
 * Radial orbital timeline — orbit layout, node selection, mobile radius sync.
 * One instance per `[data-radial-orbital]` root (section markup).
 */
(function () {
  const SEL = '[data-radial-orbital]';
  const TICK_MS = 50;

  function readBool(value, fallback) {
    if (value === 'true' || value === true) return true;
    if (value === 'false' || value === false) return false;
    return fallback;
  }

  function init(section) {
    if (!section || section.dataset.radialOrbitalInit === 'true') return;

    const orbitArea = section.querySelector('.rot-orbit-area');
    const rotator = section.querySelector('[data-rot-rotator]');
    const nodes = Array.from(section.querySelectorAll('[data-node]'));
    const cards = Array.from(section.querySelectorAll('[data-rot-card]'));

    if (!orbitArea || !rotator || !nodes.length) return;

    const cardByNodeId = {};
    cards.forEach((card) => {
      const id = card.dataset.nodeId;
      if (id) cardByNodeId[id] = card;
    });

    section.dataset.radialOrbitalInit = 'true';

    const mobileOrbitRadius = Number(section.dataset.mobileOrbitRadius) || 120;
    const settingsAutoRotate = readBool(section.dataset.autoRotate, true);
    const rotationSpeed = Number(section.dataset.rotationSpeed || 0.3) || 0.3;

    section.classList.add('rot-booting');

    let bootFadeComplete = false;
    const BOOT_FADE_MS = 700;
    const ANGLE_PHASE = 270;

    let rotationAngle = 0;
    let autoRotate = settingsAutoRotate;
    let activeNodeId = null;
    let rotationFrame = null;
    let snapFrame = null;
    let snapToken = 0;
    let openToken = 0;
    let cardDismissTimer = null;
    let lastTickTime = 0;

    let cachedRadius = 200;

    const SNAP_MS = 550;
    const CARD_DISMISS_MS = 180;

    const normalizeAngle = (deg) => ((deg % 360) + 360) % 360;

    const shortestDelta = (from, to) => {
      let delta = normalizeAngle(to - from);
      if (delta > 180) delta -= 360;
      return delta;
    };

    const easeOutCubic = (t) => 1 - (1 - t) ** 3;

    const mqMobile = window.matchMedia('(max-width: 749px)');

    let orbitFitKey = '';

    const readRadius = () => {
      cachedRadius =
        parseFloat(getComputedStyle(section).getPropertyValue('--rot-radius')) || 200;
      return cachedRadius;
    };

    const syncOrbitRadius = () => {
      if (!mqMobile.matches) {
        section.style.removeProperty('--rot-radius');
        orbitFitKey = '';
        readRadius();
        return;
      }
      const configured =
        parseFloat(getComputedStyle(section).getPropertyValue('--rot-orbit-r-mobile')) ||
        mobileOrbitRadius;

      const box = orbitArea.getBoundingClientRect();
      const innerHalf = Math.min(box.width, box.height) / 2;

      const stackEl = nodes[0] && nodes[0].querySelector('.rot-node-stack');
      const stackW = stackEl ? stackEl.getBoundingClientRect().width : 0;
      const halfStack = stackW > 0 ? stackW / 2 : 31.5;
      const glowSlack = 12;
      const activeSlack = 3;
      const radialPadding = halfStack + glowSlack + activeSlack;

      const cap = Math.max(66, Math.floor(innerHalf - radialPadding));
      const r = Math.min(configured, cap);

      const nextKey = `${configured}:${innerHalf.toFixed(1)}:${halfStack.toFixed(1)}`;
      if (nextKey === orbitFitKey) return;
      orbitFitKey = nextKey;
      section.style.setProperty('--rot-radius', `${r}px`);
      readRadius();
    };

    const normaliseTitle = (value) => String(value || '').trim().toLowerCase();

    const titleMap = {};

    nodes.forEach((node) => {
      const title = normaliseTitle(node.dataset.title);
      if (title) {
        titleMap[title] = node.dataset.id;
      }
    });

    const getNodeById = (id) => nodes.find((node) => node.dataset.id === id);

    const getRelatedIds = (node) => {
      const relatedRaw = node.dataset.related || '';

      return relatedRaw
        .split(',')
        .map((item) => normaliseTitle(item))
        .filter(Boolean)
        .map((title) => titleMap[title])
        .filter(Boolean);
    };

    const getBaseAngle = (index, total) => ((index / total) * 360 + ANGLE_PHASE) % 360;

    const getEffectiveAngle = (index, total) =>
      (getBaseAngle(index, total) + rotationAngle) % 360;

    const applyOrbitAngle = () => {
      section.style.setProperty('--rot-orbit-deg', `${rotationAngle}deg`);
    };

    const layoutNodeFixed = (node, index, total) => {
      const angle = getBaseAngle(index, total);
      const radian = (angle * Math.PI) / 180;
      const radius = cachedRadius;

      const x = radius * Math.cos(radian);
      const y = radius * Math.sin(radian);

      node.style.setProperty('--rot-node-x', `${x}px`);
      node.style.setProperty('--rot-node-y', `${y}px`);
      node.dataset.baseAngle = String(angle);
    };

    const updateNodeDepth = (node, index, total) => {
      const angle = getEffectiveAngle(index, total);
      const radian = (angle * Math.PI) / 180;
      const zIndex = Math.round(100 + 50 * Math.cos(radian));
      const isActive = node.dataset.id === activeNodeId;

      node.style.zIndex = isActive ? '250' : String(zIndex);
    };

    const layoutNodeGlow = (node) => {
      const energy = Number(node.dataset.energy || 50);
      const glow = node.querySelector('.rot-node-glow');

      if (!glow) return;

      const glowSize = energy * 0.45 + 48;
      const scaled = mqMobile.matches ? glowSize * (35 / 51) : glowSize;
      glow.style.width = `${scaled}px`;
      glow.style.height = `${scaled}px`;
    };

    const layoutNodes = () => {
      const total = nodes.length;

      nodes.forEach((node, index) => {
        layoutNodeFixed(node, index, total);
        layoutNodeGlow(node);
        updateNodeDepth(node, index, total);

        if (bootFadeComplete) {
          node.style.opacity = '1';
        } else {
          node.style.removeProperty('opacity');
        }
      });

      applyOrbitAngle();
    };

    const updateOrbitDepth = () => {
      const total = nodes.length;
      nodes.forEach((node, index) => updateNodeDepth(node, index, total));
    };

    const layoutOrbit = () => {
      syncOrbitRadius();
      layoutNodes();
    };

    const cancelCardDismiss = () => {
      if (cardDismissTimer) {
        window.clearTimeout(cardDismissTimer);
        cardDismissTimer = null;
      }
    };

    const cancelOpenSequence = () => {
      openToken += 1;
      cancelCardDismiss();
    };

    const cancelSnap = () => {
      snapToken += 1;
      if (snapFrame) {
        cancelAnimationFrame(snapFrame);
        snapFrame = null;
      }
      section.classList.remove('is-snapping');
    };

    const stopRotation = () => {
      if (rotationFrame) {
        cancelAnimationFrame(rotationFrame);
        rotationFrame = null;
      }
      lastTickTime = 0;
      section.classList.remove('is-orbiting');
    };

    const tickRotation = (time) => {
      if (!autoRotate || !bootFadeComplete) {
        rotationFrame = null;
        lastTickTime = 0;
        return;
      }

      if (!lastTickTime) {
        lastTickTime = time;
      }

      const deltaMs = Math.min(time - lastTickTime, 64);
      lastTickTime = time;

      if (deltaMs > 0) {
        rotationAngle = (rotationAngle + (rotationSpeed * deltaMs) / TICK_MS) % 360;
        applyOrbitAngle();
        updateOrbitDepth();
      }

      rotationFrame = requestAnimationFrame(tickRotation);
    };

    const startRotation = () => {
      cancelOpenSequence();
      cancelSnap();
      stopRotation();

      if (!autoRotate || !bootFadeComplete) return;

      section.classList.add('is-orbiting');
      lastTickTime = 0;
      rotationFrame = requestAnimationFrame(tickRotation);
    };

    const anyCardVisible = () => cards.some((card) => card.classList.contains('is-active'));

    const hideAllCards = () => {
      cards.forEach((card) => {
        // Instant hide; keep left/top so nothing flashes at 0,0
        card.style.transition = 'none';
        card.classList.remove('is-active');
        void card.offsetWidth;
        card.style.removeProperty('transition');
      });
    };

    const clearCardPositions = () => {
      cards.forEach((card) => {
        card.style.removeProperty('left');
        card.style.removeProperty('top');
      });
    };

    const positionCardBelowNode = (card, node) => {
      const areaRect = orbitArea.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      const left = nodeRect.left + nodeRect.width / 2 - areaRect.left;
      const top = nodeRect.bottom - areaRect.top + 10;

      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
    };

    const showCardForNode = (node) => {
      const card = cardByNodeId[node.dataset.id];
      if (!card) return;

      hideAllCards();
      clearCardPositions();
      positionCardBelowNode(card, node);

      requestAnimationFrame(() => {
        if (activeNodeId !== node.dataset.id) return;
        positionCardBelowNode(card, node);
        card.classList.add('is-active');
      });
    };

    const syncActiveCardPosition = () => {
      if (!activeNodeId) return;
      const node = getNodeById(activeNodeId);
      const card = cardByNodeId[activeNodeId];
      if (!node || !card || !card.classList.contains('is-active')) return;
      positionCardBelowNode(card, node);
    };

    const animateNodeToTop = (targetDeg, onComplete) => {
      cancelSnap();
      stopRotation();

      const startDeg = rotationAngle;
      const delta = shortestDelta(startDeg, targetDeg);
      const token = snapToken;

      if (Math.abs(delta) < 0.35) {
        rotationAngle = normalizeAngle(targetDeg);
        applyOrbitAngle();
        updateOrbitDepth();
        if (onComplete) onComplete();
        return;
      }

      section.classList.add('is-snapping');
      const t0 = performance.now();

      const tick = (now) => {
        if (token !== snapToken) return;

        const t = Math.min(1, (now - t0) / SNAP_MS);
        rotationAngle = normalizeAngle(startDeg + delta * easeOutCubic(t));
        applyOrbitAngle();
        updateOrbitDepth();

        if (t < 1) {
          snapFrame = requestAnimationFrame(tick);
          return;
        }

        snapFrame = null;
        rotationAngle = normalizeAngle(targetDeg);
        applyOrbitAngle();
        updateOrbitDepth();
        section.classList.remove('is-snapping');
        if (onComplete) onComplete();
      };

      snapFrame = requestAnimationFrame(tick);
    };

    const clearActiveState = () => {
      cancelOpenSequence();
      cancelSnap();
      activeNodeId = null;

      nodes.forEach((node) => {
        node.classList.remove('is-active', 'is-related', 'is-pulsing');
      });
      hideAllCards();
      clearCardPositions();

      if (settingsAutoRotate) {
        autoRotate = true;
        startRotation();
      }

      layoutOrbit();
    };

    const openNode = (id) => {
      const selectedNode = getNodeById(id);
      if (!selectedNode) return;

      const wasActive = activeNodeId === id;

      if (wasActive) {
        clearActiveState();
        return;
      }

      // 1) Kill any in-flight open; hide current popup completely first
      cancelOpenSequence();
      cancelSnap();
      const sequence = openToken;
      const hadVisibleCard = anyCardVisible();

      autoRotate = false;
      stopRotation();
      hideAllCards();

      const runSnapThenReveal = () => {
        if (sequence !== openToken) return;

        clearCardPositions();

        activeNodeId = id;

        nodes.forEach((node) => {
          node.classList.remove('is-active', 'is-related', 'is-pulsing');
        });
        selectedNode.classList.add('is-active');

        getRelatedIds(selectedNode).forEach((relatedId) => {
          const relatedNode = getNodeById(relatedId);
          if (relatedNode) {
            relatedNode.classList.add('is-related', 'is-pulsing');
          }
        });

        layoutOrbit();

        const nodeIndex = Number(selectedNode.dataset.index || 0);
        const targetRotation = normalizeAngle(360 - (nodeIndex / nodes.length) * 360);

        // 2) Node travels to top — no popup yet
        animateNodeToTop(targetRotation, () => {
          if (sequence !== openToken || activeNodeId !== id) return;
          // 3) Settled — show this node's popup
          showCardForNode(selectedNode);
        });
      };

      if (hadVisibleCard) {
        cancelCardDismiss();
        cardDismissTimer = window.setTimeout(() => {
          cardDismissTimer = null;
          runSnapThenReveal();
        }, CARD_DISMISS_MS);
        return;
      }

      runSnapThenReveal();
    };

    const onResize = () => {
      layoutOrbit();
      syncActiveCardPosition();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopRotation();
        return;
      }

      if (autoRotate && bootFadeComplete && !activeNodeId) {
        startRotation();
      }
    };

    nodes.forEach((node) => {
      node.addEventListener('click', (event) => {
        if (event.target.closest('[data-rot-card-close]')) return;
        event.stopPropagation();
        openNode(node.dataset.id);
      });
    });

    section.querySelectorAll('[data-rot-card-close]').forEach((closeBtn) => {
      closeBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        clearActiveState();
      });
    });

    orbitArea.addEventListener('click', (event) => {
      const onNode = event.target.closest('[data-node]');
      const onCard = event.target.closest('.rot-card');
      if (!onNode && !onCard) {
        clearActiveState();
      }
    });

    layoutOrbit();
    requestAnimationFrame(() => {
      layoutOrbit();
      requestAnimationFrame(() => {
        section.classList.remove('rot-booting');
        window.setTimeout(() => {
          bootFadeComplete = true;
          nodes.forEach((node) => {
            node.style.opacity = '1';
          });
          layoutNodes();
          startRotation();
        }, BOOT_FADE_MS);
      });
    });

    window.addEventListener('resize', onResize);
    mqMobile.addEventListener('change', onResize);
    document.addEventListener('visibilitychange', onVisibilityChange);

    const orbitResizeObserver = new ResizeObserver(() => {
      layoutOrbit();
      syncActiveCardPosition();
    });
    orbitResizeObserver.observe(orbitArea);

    section._radialOrbitalDestroy = () => {
      cancelOpenSequence();
      cancelSnap();
      stopRotation();
      window.removeEventListener('resize', onResize);
      mqMobile.removeEventListener('change', onResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      orbitResizeObserver.disconnect();
      delete section._radialOrbitalDestroy;
      delete section.dataset.radialOrbitalInit;
    };
  }

  function destroy(section) {
    if (section && typeof section._radialOrbitalDestroy === 'function') {
      section._radialOrbitalDestroy();
    }
  }

  function scan(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SEL).forEach(init);
  }

  function boot() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target && ev.target.querySelectorAll) scan(ev.target);
  });

  document.addEventListener('shopify:section:unload', function (ev) {
    if (!ev.target || !ev.target.querySelectorAll) return;
    ev.target.querySelectorAll(SEL).forEach(destroy);
  });
})();
